import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useExchangeRate } from '../ExchangeRateContext';
import { useAuth } from '../contexts/AuthContext';
import Loader from './Loader';
import Pagination from './Pagination';
import PaymentMethodSelector from './PaymentMethodSelector';
import MultiplePaymentModal from './MultiplePaymentModal';

const Customer3SalesHistoryModal = ({
  show,
  onHide,
  selectedCustomer3,
  allCustomer3s,
  onCustomerSelect,
  currentSale = null,
  onConfirmSale = null,
  deliveryPrice = 0,
  onUpdateCartItem = null,
  onRemoveCartItem = null,
  showFirstPriceSwitch = false,
  isFirstPrice = false,
  onToggleFirstPrice = null,
  setDeliveryPrice = null,
  loading = false // Submission loading
}) => {
  const [sellBatches, setSellBatches] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const [debts, setDebts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totalDebt, setTotalDebt] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [remainingDebt, setRemainingDebt] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [showMultiplePaymentModal, setShowMultiplePaymentModal] = useState(false);
  const [hybridPayments, setHybridPayments] = useState([]);
  const [expandedSales, setExpandedSales] = useState(new Set());
  const [deliveryPrices, setDeliveryPrices] = useState({});
  const [balance, setBalance] = useState(0);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [serverHistory, setServerHistory] = useState([]);
  const [saleDetails, setSaleDetails] = useState({}); // Cache for sale details by batch_id
  const [loadingSaleDetails, setLoadingSaleDetails] = useState(new Set()); // Track which sales are loading
  const exchangeRate = useExchangeRate();
  const { token, user } = useAuth();
  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'X-Owner-Id': String(user?.ownerId ?? ''),
    'X-Staff-Id': String(user?.staffId ?? ''),
  };

  // Make first price switch ON by default when modal opens
  useEffect(() => {
    if (show && showFirstPriceSwitch && typeof onToggleFirstPrice === 'function' && !isFirstPrice && currentSale && selectedCustomer3) {
      // Turn ON the switch if not already ON
      onToggleFirstPrice();
    }
    // eslint-disable-next-line
  }, [show, showFirstPriceSwitch, isFirstPrice, currentSale, selectedCustomer3]);

  // Reset pagination on customer change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCustomer3]);

  // Remove delivery logic and show toast if deliveryPrice is set
  useEffect(() => {
    if (show && deliveryPrice > 0 && typeof setDeliveryPrice === 'function') {
      setDeliveryPrice(0);
      toast.error("3-tur mijozda yetkazib berish ishlatilamydi");
    }
    // eslint-disable-next-line
  }, [show, deliveryPrice]);

  // Fetch sell batches when customer3 is selected
  useEffect(() => {
    if (!selectedCustomer3) {
      setSellBatches([]);
      setDebts([]);
      setPayments([]);
      setTotalDebt(0);
      setTotalPayments(0);
      setRemainingDebt(0);
      setBalance(0);
      setLoadingBalance(false);
      setServerHistory([]);
      setServerTotalItems(0);
      return;
    }

    setIsFetching(true);
    setError('');
    setLoadingBalance(true);

    // Fetch customer3 balance from backend
    fetch(`/api/statistics/customer3/${selectedCustomer3.id}/balance`, { headers: authHeaders })
      .then(res => { if (!res.ok) throw new Error('Failed to fetch balance'); return res.json(); })
      .then(data => {
        const b = Number(data.balance || 0);
        setBalance(b);
        if (typeof data.totalDebt !== 'undefined') setTotalDebt(Number(data.totalDebt) || 0);
        if (typeof data.totalPayments !== 'undefined') setTotalPayments(Number(data.totalPayments) || 0);
        setRemainingDebt((Number(data.totalDebt || 0)) - (Number(data.totalPayments || 0)));
      })
      .catch(err => { console.error(err); })
      .finally(() => setLoadingBalance(false));

    // Unified history (server-side pagination)
    fetch(`/api/customer3-history/${selectedCustomer3.id}?page=1&limit=${itemsPerPage}`, { headers: authHeaders })
      .then(res => res.json())
      .then(data => {
        setServerHistory(Array.isArray(data?.items) ? data.items : []);
        setServerTotalItems(Number(data?.total) || 0);
      })
      .catch(() => {
        setServerHistory([]);
        setServerTotalItems(0);
      })
      .finally(() => setIsFetching(false));
  }, [selectedCustomer3]);

  // Page change effect (server paging)
  useEffect(() => {
    if (!selectedCustomer3) return;
    setIsFetching(true);
    fetch(`/api/customer3-history/${selectedCustomer3.id}?page=${currentPage}&limit=${itemsPerPage}`, { headers: authHeaders })
      .then(res => res.json())
      .then(data => {
        setServerHistory(Array.isArray(data?.items) ? data.items : []);
        setServerTotalItems(Number(data?.total) || 0);
      })
      .catch(() => {
        setServerHistory([]);
        setServerTotalItems(0);
      })
      .finally(() => setIsFetching(false));
  }, [currentPage, itemsPerPage]);

  // Fetch delivery prices for all sell batches
  useEffect(() => {
    if (!sellBatches || sellBatches.length === 0) return;
    const fetchAll = async () => {
      const prices = {};
      await Promise.all(sellBatches.map(async batch => {
        if (batch.sellBatchID) {
          const res = await fetch(`/api/sales/delivery/${batch.sellBatchID}`, { headers: authHeaders });
          const data = await res.json();
          prices[batch.sellBatchID] = data?.delivery_price || 0;
        }
      }));
      setDeliveryPrices(prices);
    };
    fetchAll();
  }, [sellBatches]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('uz-UZ', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get display price for an item based on customer currency
  const getDisplayPrice = (item) => {
    // ROBUST SOLUTION: If Customer3 currency is USD and price_usd is available, use it directly
    if (selectedCustomer3 && selectedCustomer3.currency === 'USD' && item.price_usd !== undefined) {
      return item.price_usd;
    }

    // Legacy fallback: If first price is enabled and customer3's currency is USD, use first_price directly in USD
    if (isFirstPrice && selectedCustomer3 && selectedCustomer3.currency === 'USD' && item.first_price) {
      return item.first_price;
    }

    if (!selectedCustomer3 || !selectedCustomer3.currency) return item.price;
    const customerCurrency = selectedCustomer3.currency;
    const batchCurrency = item.currency || 'UZS';
    if (customerCurrency === batchCurrency) return item.price;
    if (customerCurrency === 'USD' && batchCurrency === 'UZS') {
      // Convert UZS to USD
      return +(item.price / exchangeRate).toFixed(2);
    }
    if (customerCurrency === 'UZS' && batchCurrency === 'USD') {
      // Convert USD to UZS
      return Math.round(item.price * exchangeRate);
    }
    return item.price;
  };


  // Helper to get display currency label
  const getDisplayCurrency = () => {
    if (!selectedCustomer3 || !selectedCustomer3.currency) return 'UZS';
    return selectedCustomer3.currency;
  };

  // Update formatCurrency to use correct currency
  const formatCurrency = (amount, currency) => {
    const displayCurrency = currency || getDisplayCurrency();
    if (displayCurrency === 'USD') {
      return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
    }
    return `${Number(amount).toLocaleString()} UZS`;
  };

  const handlePayment = async (paymentsToUse = null) => {
    if (!selectedCustomer3) return;
    
    // Validate payment method for UZS currency
    const customerCurrency = selectedCustomer3.currency || 'UZS';
    if (customerCurrency === 'UZS' && !paymentMethod) {
      toast.error('To\'lov usulini tanlang');
      return;
    }
    
    // Determine if using hybrid payments or single payment
    const isHybrid = paymentMethod === 'hybrid';
    const payments = paymentsToUse || (isHybrid && hybridPayments.length > 0 ? hybridPayments : null);
    
    if (isHybrid && (!payments || payments.length === 0)) {
      toast.error('Gibrid to\'lovlarni kiriting');
      setShowMultiplePaymentModal(true);
      return;
    }
    
    const amountToPay = payments ? payments.reduce((sum, p) => sum + Number(p.amount || 0), 0) : Number(paymentAmount);
    if (!amountToPay || amountToPay <= 0) return;
    
    setLoadingPayment(true);
    setError('');
    try {
      // Create customer3 payment
      const resp = await fetch('/api/customer3-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          customer_id: selectedCustomer3.id,
          customer_type: 'customer3',
          amount: amountToPay
        })
      });
      if (!resp.ok) throw new Error('To\'lovni yaratib bo\'lmadi');

      // Record payment transactions
      const paymentMethodMap = {
        'cash': 0,
        'terminal': 1,
        'card': 2,
        'dollars': 3
      };
      
      if (payments && payments.length > 0) {
        // Handle split payments - execute sequentially
        for (const payment of payments) {
          const paymentType = paymentMethodMap[payment.method] || 0;
          try {
            await fetch('/api/payment-transactions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...authHeaders
              },
              body: JSON.stringify({
                customer_type: 3, // Customer3
                action: true, // payment
                type: paymentType,
                amount: Number(payment.amount || 0),
                ownerId: user?.ownerId || user?.id,
                staffId: user?.staffId,
              })
            });
          } catch (error) {
            console.error('Error recording payment transaction:', error);
            // Continue with other payments even if one fails
          }
        }
      } else {
        // Single payment
        const paymentType = customerCurrency === 'USD' ? 3 : (paymentMethodMap[paymentMethod] || 0);
        try {
          await fetch('/api/payment-transactions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders
            },
            body: JSON.stringify({
              customer_type: 3, // Customer3
              action: true, // payment
              type: paymentType,
              amount: amountToPay,
              ownerId: user?.ownerId || user?.id,
              staffId: user?.staffId,
            })
          });
        } catch (error) {
          console.error('Error recording payment transaction:', error);
          // Don't fail the payment if transaction recording fails
        }
      }

      toast.success('To\'lov qo\'shildi');
      setPaymentAmount('');
      setPaymentMethod('');
      setHybridPayments([]);
      setShowMultiplePaymentModal(false);
      
      // Refresh debts and payments
      const [debtData, paymentData] = await Promise.all([
        fetch(`/api/customer3-debts/customer3/${selectedCustomer3.id}`, { headers: authHeaders }).then(res => res.json()),
        fetch(`/api/customer3-payments/customer3/${selectedCustomer3.id}`, { headers: authHeaders }).then(res => res.json())
      ]);

      setDebts(debtData.debts || []);
      setPayments(paymentData.payments || []);
      setTotalDebt(debtData.totalDebt || 0);
      setTotalPayments(paymentData.totalPayments || 0);
      setRemainingDebt(debtData.remainingDebt || 0);

      // Refresh backend-calculated balance
      try {
        setLoadingBalance(true);
        const balRes = await fetch(`/api/statistics/customer3/${selectedCustomer3.id}/balance`, { headers: authHeaders });
        if (balRes.ok) {
          const data = await balRes.json();
          setBalance(Number(data.balance || 0));
        }
      } catch (e) {
        console.error('Failed to refresh balance', e);
      } finally {
        setLoadingBalance(false);
      }
      
      // Refresh server history to show new payment immediately
      try {
        const historyRes = await fetch(`/api/customer3-history/${selectedCustomer3.id}?page=${currentPage}&limit=${itemsPerPage}`, { headers: authHeaders });
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setServerHistory(Array.isArray(historyData?.items) ? historyData.items : []);
          setServerTotalItems(Number(historyData?.total) || 0);
        }
      } catch (e) {
        console.error('Failed to refresh history', e);
      }
    } catch (err) {
      setError(err.message || 'Xatolik yuz berdi');
    } finally {
      setLoadingPayment(false);
    }
  };

  const setPaymentToCurrentSale = () => {
    if (currentSale && currentSale.length > 0) {
      const total = currentSale.reduce((sum, item) => sum + (getDisplayPrice(item) * item.qty), 0);
      setPaymentAmount(total.toString());
    }
  };

  // Fetch sale details for a specific batch
  const fetchSaleDetails = async (batchId) => {
    if (saleDetails[batchId]) {
      return; // Already cached
    }

    setLoadingSaleDetails(prev => new Set([...prev, batchId]));
    try {
      const response = await fetch(`/api/sale-history/by-sellbatch/${batchId}`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setSaleDetails(prev => ({
          ...prev,
          [batchId]: Array.isArray(data) ? data : []
        }));
      } else {
        console.error('Failed to fetch sale details for batch:', batchId);
        setSaleDetails(prev => ({
          ...prev,
          [batchId]: []
        }));
      }
    } catch (error) {
      console.error('Error fetching sale details:', error);
      setSaleDetails(prev => ({
        ...prev,
        [batchId]: []
      }));
    } finally {
      setLoadingSaleDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(batchId);
        return newSet;
      });
    }
  };

  const toggleSaleDetails = (batchId) => {
    const newExpanded = new Set(expandedSales);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
      // Fetch sale details when expanding
      fetchSaleDetails(batchId);
    }
    setExpandedSales(newExpanded);
  };

  // Balance is fetched from backend via /api/statistics

  // Combine sells, refunds and payments chronologically (server already paginated)
  const getChronologicalHistory = () => {
    // Map server items to the structure used by the UI
    return (serverHistory || []).map(it => ({
      type: it.type,
      date: it.date,
      amount: Number(it.amount) || 0,
      batchId: it.batch_id || null,
      paymentId: it.payment_id || null,
      doerName: it.doer_name || 'EGA',
      // For expand, we only know count for sells; refunds show one item; payments have none
      items: it.type === 'sell' ? (it.items || []) : (it.type === 'refund' ? [{ productName: '', quantity: 0, sellingPrice: 0, currency: '' }] : [])
    }));
  };

  // Expandable sale details component
  const SaleDetails = ({ sale }) => {
    const customerCurrency = getDisplayCurrency();
    const batchId = sale.batchId;
    const items = saleDetails[batchId] || [];
    const isLoading = loadingSaleDetails.has(batchId);

    if (isLoading) {
      return (
        <div className="mt-2 p-2 bg-light text-center">
          <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
          <small className="text-muted">Mahsulot tafsilotlari yuklanmoqda...</small>
        </div>
      );
    }

    if (!items || !Array.isArray(items) || items.length === 0) {
      return (
        <div className="mt-2 p-2 bg-light">
          <small className="text-muted">Mahsulot tafsilotlari mavjud emas</small>
        </div>
      );
    }

    return (
      <div className="mt-2">
        <div className="table-responsive">
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Mahsulot</th>
                <th>Narx</th>
                <th>Miqdor</th>
                <th>Summa</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                // Map backend fields to frontend fields
                let price = item.SalePricePerUnit || item.salepriceperunit || item.sellingPrice || 0;
                let currency = item.Currency || item.currency || 'UZS';
                let quantity = item.QuantitySold || item.quantitysold || item.quantity || 0;
                let productName = item.product_name || item.productName || 'Noma\'lum mahsulot';

                if (customerCurrency === 'USD' && item.price_usd !== undefined) {
                  price = item.price_usd;
                  currency = 'USD';
                } else if (isFirstPrice && customerCurrency === 'USD' && item.first_price) {
                  // Legacy fallback: For Customer3 USD with first_price, use first_price directly
                  price = item.first_price;
                  currency = 'USD';
                } else if (customerCurrency === 'USD' && currency === 'UZS') {
                  price = +(price / exchangeRate).toFixed(2);
                  currency = 'USD';
                } else if (customerCurrency === 'UZS' && currency === 'USD') {
                  price = Math.round(price * exchangeRate);
                  currency = 'UZS';
                }
                return (
                  <tr key={idx}>
                    <td>
                      {productName}
                      {item.barcode && (
                        <small className="d-block text-muted">
                          Barcode: {item.barcode}
                        </small>
                      )}
                    </td>
                    <td>{formatCurrency(price, currency)}</td>
                    <td>{quantity}</td>
                    <td>{formatCurrency(quantity * price, currency)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-end mt-2">
          <strong>Jami: {formatCurrency(items.reduce((sum, item) => {
            // Map backend fields to frontend fields
            let price = item.SalePricePerUnit || item.salepriceperunit || item.sellingPrice || 0;
            let currency = item.Currency || item.currency || 'UZS';
            let quantity = item.QuantitySold || item.quantitysold || item.quantity || 0;

            if (customerCurrency === 'USD' && item.price_usd !== undefined) {
              price = item.price_usd;
            } else if (isFirstPrice && customerCurrency === 'USD' && item.first_price) {
              price = item.first_price;
            } else if (customerCurrency === 'USD' && currency === 'UZS') {
              price = +(price / exchangeRate).toFixed(2);
            } else if (customerCurrency === 'UZS' && currency === 'USD') {
              price = Math.round(price * exchangeRate);
            }
            return sum + (quantity * price);
          }, 0), customerCurrency)}</strong>
        </div>
      </div>
    );
  };

  // Calculate overal in customer currency
  const overal = currentSale
    ? currentSale.reduce((sum, item) => sum + (getDisplayPrice(item) * item.qty), 0)
    : 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>3-turi Mijoz Sotuv Tarixi</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Customer Selection and Balance */}
        <div className="mb-3">
          <div className="row align-items-center">
            <div className="col-md-6">
              <label className="form-label">Mijozni tanlang</label>
              <select
                className="form-select"
                value={selectedCustomer3 ? selectedCustomer3.id : ''}
                onChange={e => {
                  const customer = allCustomer3s.find(c => c.id === Number(e.target.value));
                  onCustomerSelect(customer);
                }}
              >
                <option value="">Tanlang...</option>
                {allCustomer3s.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.phone})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              {selectedCustomer3 && (
                <div className="text-end">
                  <h6>Balans</h6>
                  {loadingBalance ? (
                    <Loader />
                  ) : (
                    <h4 className={balance >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(Math.abs(balance), getDisplayCurrency())} {balance >= 0 ? '(Kredit)' : '(Qarz)'}
                    </h4>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {/* Current Sale Details */}
        {currentSale && selectedCustomer3 && (
          <div className="mb-4">
            <h6 className="border-bottom pb-2">Joriy sotuv</h6>
            {showFirstPriceSwitch && (
              <div className="form-check form-switch mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="firstPriceSwitch"
                  checked={isFirstPrice}
                  onChange={onToggleFirstPrice}
                />
                <label className="form-check-label ms-2" htmlFor="firstPriceSwitch">
                  Narxni birinchi narxga (first_price) o'zgartirish
                </label>
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th>Mahsulot</th>
                    <th>Narx</th>
                    <th>Miqdor</th>
                    <th>Summa</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {currentSale.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.name}{item.batch_barcode ? ` (${item.batch_barcode})` : ''}</td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={selectedCustomer3 && selectedCustomer3.currency === 'USD' && item.price_usd !== undefined ? item.price_usd : getDisplayPrice(item)}
                          min={0}
                          onChange={e => {
                            const value = Number(e.target.value);
                            if (!onUpdateCartItem) return;
                            if (selectedCustomer3 && selectedCustomer3.currency === 'USD') {
                              onUpdateCartItem(idx, { price_usd: value });
                            } else {
                              onUpdateCartItem(idx, { price: value });
                            }
                          }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={item.qty}
                          min={1}
                          onChange={e => {
                            if (onUpdateCartItem) onUpdateCartItem(idx, { qty: Math.max(1, Number(e.target.value)) });
                          }}
                        />
                      </td>
                      <td>{formatCurrency((item.qty * getDisplayPrice(item)), getDisplayCurrency())}</td>
                      <td>
                        {onRemoveCartItem && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => onRemoveCartItem(idx)}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-end">
              <strong>
                Jami: {formatCurrency(overal, getDisplayCurrency())}
              </strong>
            </div>
          </div>
        )}

        {/* Payment Section */}
        {selectedCustomer3 && (
          <div className="mb-4">
            <h6 className="border-bottom pb-2">To'lov qo'shish</h6>
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="number"
                  className="form-control"
                  placeholder="To'lov miqdori..."
                  value={paymentAmount}
                  onChange={e => setPaymentAmount(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-outline-primary w-100"
                  onClick={setPaymentToCurrentSale}
                  disabled={!currentSale || currentSale.length === 0}
                >
                  Joriy sotuv narxi
                </button>
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-success w-100"
                  onClick={() => handlePayment()}
                  disabled={loadingPayment || !paymentAmount}
                >
                  {loadingPayment ? 'Qo\'shilmoqda...' : 'To\'lov qo\'shish'}
                </button>
              </div>
            </div>
            {selectedCustomer3.currency === 'UZS' && Number(paymentAmount) > 0 && (
              <div className="mb-3">
                <label className="form-label small mb-2">To'lov usuli</label>
                {paymentMethod === 'hybrid' ? (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge bg-primary p-2">Gibrid to'lov tanlangan</span>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => setShowMultiplePaymentModal(true)}
                    >
                      To'lovlarni ko'rish/tahrirlash
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => {
                        setPaymentMethod('');
                        setHybridPayments([]);
                      }}
                    >
                      Bekor qilish
                    </Button>
                  </div>
                ) : (
                  <PaymentMethodSelector
                    value={paymentMethod}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPaymentMethod(value);
                      if (value === 'hybrid') {
                        setShowMultiplePaymentModal(true);
                      }
                    }}
                    onSplitClick={() => {
                      setPaymentMethod('hybrid');
                      setShowMultiplePaymentModal(true);
                    }}
                    showSplit={true}
                  />
                )}
              </div>
            )}
          </div>
        )}
        <MultiplePaymentModal
          show={showMultiplePaymentModal}
          onHide={() => setShowMultiplePaymentModal(false)}
          onConfirm={(payments) => {
            setHybridPayments(payments);
            setShowMultiplePaymentModal(false);
            // Don't auto-submit - user will click submit button
          }}
          totalAmount={Number(paymentAmount) || 0}
          currency="UZS"
        />

        {/* Loading State */}
        {isFetching && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Yuklanmoqda...</span>
            </div>
            <div className="mt-2">Yuklanmoqda...</div>
          </div>
        )}

        {/* Chronological History */}
        {!isFetching && !error && selectedCustomer3 && (
          <div className="mb-4">
            <h6 className="border-bottom pb-2">Sotuv, qaytarish va to'lovlar tarixi</h6>
            {(() => {
              const pageItems = getChronologicalHistory();
              const totalItems = serverTotalItems;
              const customerCurrency = getDisplayCurrency();
              if (totalItems === 0) {
                return <p className="text-muted">Tarix yo'q</p>;
              }
              return (
                <>
                  <div className="border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {pageItems.map((item, idx) => (
                      <div key={`${item.type}-${idx}-${item.date}`} className={`card mb-2 ${item.type === 'payment' ? 'border-success' : (item.type === 'refund' ? 'border-danger' : 'border-warning')}`}>
                        <div className="card-body p-2">
                          <div className="row align-items-center">
                            <div className="col-md-2">
                              <small className="text-muted">{formatDate(item.date)}</small>
                            </div>
                            <div className="col-md-2">
                              <span className={`badge ${item.type === 'payment' ? 'bg-success' : (item.type === 'refund' ? 'bg-danger' : 'bg-warning')}`}>
                                {item.type === 'payment' ? 'To\'lov' : (item.type === 'refund' ? 'Qaytarish' : 'Sotuv')}
                              </span>
                            </div>
                            <div className="col-md-4">
                              {item.type === 'payment'
                                ? (
                                  <div>
                                    <div>{`To'lov #${item.paymentId}`}</div>
                                    <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                                  </div>
                                )
                                : item.type === 'refund' ? (
                                  <div>
                                    <div>Qaytarish #{item.batchId}</div>
                                    <small className="text-muted d-block">1 mahsulot</small>
                                    <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                                  </div>
                                ) : (
                                  <div>
                                    <div>Sotuv #{item.batchId}</div>
                                    <small className="text-muted d-block">Bajaruvchi: {item.doerName || 'EGA'}</small>
                                  </div>
                                )
                              }
                            </div>
                            <div className="col-md-2 text-end">
                              <strong>{formatCurrency(item.amount, customerCurrency)}</strong>
                            </div>
                            <div className="col-md-2 text-end">
                              {(item.type === 'sell' || item.type === 'refund') && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => toggleSaleDetails(item.batchId)}
                                >
                                  {expandedSales.has(item.batchId) ? 'Yashirish' : 'Batafsil'}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Show delivery price for sell batches */}
                          {item.type === 'sell' && (
                            <div className="text-end text-primary">
                              {deliveryPrices[item.batchId] > 0 ? (
                                <strong>
                                  Jami: {formatCurrency(item.amount, customerCurrency)}
                                  {' + '}
                                  {formatCurrency(deliveryPrices[item.batchId], customerCurrency)} (Yetkazib berish)
                                  {' = '}
                                  {formatCurrency(item.amount + deliveryPrices[item.batchId], customerCurrency)}
                                </strong>
                              ) : (
                                <strong>Jami: {formatCurrency(item.amount, customerCurrency)}</strong>
                              )}
                            </div>
                          )}
                          {/* Expandable sale details */}
                          {(item.type === 'sell' || item.type === 'refund') && expandedSales.has(item.batchId) && (
                            <SaleDetails sale={item} />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(page) => setCurrentPage(page)}
                    onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                  />
                </>
              );
            })()}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {currentSale && onConfirmSale && (
          <Button
            variant="success"
            onClick={onConfirmSale}
            disabled={loading}
            className="me-2"
          >
            {loading ? 'Yuborilmoqda...' : 'Sotuvni tasdiqlash'}
          </Button>
        )}
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Yopish
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Customer3SalesHistoryModal; 